import {
	getPropertySortText,
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle
} from "client/Components";
import {
	convertSortDirectionToDto,
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	RemoteDataSourceState,
	useUserTimezone,
	useValueEqualMemo
} from "client/Hooks";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import { navigateToUriInPopOutWindow } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	EgnyteGlobalSearchResultItem,
	EgnyteOrderModel,
	EgnyteSortProperty,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchEgnyte,
	SearchIntegrationKind,
	SharePointSearchTimeScope
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { SearchSettings } from "../SearchSettings";
import { HttpRequestError } from "client/Api/httpClient";
import { AuthenticationRequiredResult } from "client/Api/Egnyte/egnyteConfiguration";
import { ScopeLayout } from "../ScopeLayout";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { EgnyteItemView } from "../Items/EgnyteItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type EgnyteSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const EgnyteSearch = (props: EgnyteSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [authenticationState, setAuthenticationState] = useState<AuthenticationState | null>(null);

	const [formData, setFormData] = useState<EgnyteSearchFormData>({
		...defaultFilters,
		sort: undefined,
	});

	const listHandle: RefObject<ScrollableVirtualListHandle<ResultItem> | null> = useRef<ScrollableVirtualListHandle<ResultItem>>(null);
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<EgnyteSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);

	const partialDataSourceOptions: Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> = useValueEqualMemo(
		() => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				timeScope: formData.timeScope
			},
			sorting: formData.sort,
			globalContext: {
				searchVersion: searchTerm.version
			}
		}),
		[
			searchTerm,
			formData.sort,
			formData.timeScope
		]
	);

	const dataLoadingErrorHandler = useCallback(
		(e: unknown): void => {
			if(e instanceof HttpRequestError && e.statusCode === 409) {
				try {
					const result: AuthenticationRequiredResult = e.readJson<AuthenticationRequiredResult>();
					if (result.redirectUrl) {
						setAuthenticationState({
							url: result.redirectUrl,
							wasInvoked: false
						});
					} else {
						console.error("Unexpected result", result);
					}

				} catch (e) {
					console.error(e);
				}
			}
		},
		[]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems,
		onError: dataLoadingErrorHandler
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const authenticateConfiguration = useMemo(
		(): { onRetry: () => void; retryText: string; } | undefined => {
			if (isNil(authenticationState)) {
				return undefined;
			}

			if (authenticationState.wasInvoked) {
				return {
					onRetry: () => {
						setAuthenticationState(null);
						reload();
					},
					retryText: localize("_Finish")
				};
			}

			return {
				onRetry: (): void => {
					setAuthenticationState(
						(x) => {
							return {
								...ensureNotNil(x),
								wasInvoked: true
							};
						}
					);

					navigateToUriInPopOutWindow(authenticationState.url);
				},
				retryText: localize("_Authenticate")
			};
		},
		[authenticationState, reload]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {

		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={112}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="egnyte-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: EgnyteGlobalSearchResultItem, index: number): ReactElement => (
						<EgnyteItemView
							key={index}
							navigationIndex={index}
							item={item}
							itemScope={egnyteSearchScope}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		if (isNotNil(authenticateConfiguration)) {
			return (
				<NoResults
					{...authenticateConfiguration}
					trackingId="authentication-required"
					icon="gear"
					title={localize("_AuthenticationRequired")}
					message={{
						template: localize("_ToUseNameAuthenticationIsNeeded"),
						replacements: [
							{ what: "{{Name}}", filler: localize("_Egnyte") }
						]
					}}
				/>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
						isExpandedByDefault
					/>

					<SearchSettings.Select
						label={localize("_LastModified")}
						attachable={formHandler.for("timeScope")}
						options={timeScopeOptions}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
EgnyteSearch.displayName = "EgnyteSearch";

export const PAGE_SIZE = 20;

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: StringKeyOf<Pick<EgnyteGlobalSearchResultItem, "name" | "lastModifiedDate" | "kind">>;
} | undefined;

type EgnyteSearchFormData = {
	sort: SortValue;
	timeScope: SharePointSearchTimeScope;
};

type FilterModel = Omit<EgnyteSearchFormData, "sort">;

const defaultFilters: FilterModel = {
	timeScope: SharePointSearchTimeScope.AnyTime
};

type AuthenticationState = {
	url: string;
	wasInvoked: boolean;
};

type ResultItem = ObjectWithClientId<EgnyteGlobalSearchResultItem>;

// It appears there is a bug in the Egnyte API where the last item on a page will be included as
// the first item in the next page resulting in none unique keys
function getUniqueKey(item: ResultItem): string {
	return getClientId(item);
}

const sortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: localize("_Relevance"),
		value: undefined,
		valueTrackingId: "relevance"
	},
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateModified", "_NewToOld"),
		value: { propertyName: "lastModifiedDate", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "modified-on-new-old"
	},
	{
		text: getPropertySortText("_DateModified", "_OldToNew"),
		value: { propertyName: "lastModifiedDate", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "modified-on-old-new"
	}
];

const timeScopeOptions: readonly SelectOption<SharePointSearchTimeScope>[] = [
	{
		value: SharePointSearchTimeScope.AnyTime,
		text: localize("_AnyTime")
	},
	{
		value: SharePointSearchTimeScope.Last24Hrs,
		text: localize("_Past24hours")
	},
	{
		value: SharePointSearchTimeScope.LastWeek,
		text: localize("_PastWeek")
	},
	{
		value: SharePointSearchTimeScope.LastMonth,
		text: localize("_PastMonth")
	},
	{
		value: SharePointSearchTimeScope.Last3Months,
		text: localize("_Past3Months")
	},
	{
		value: SharePointSearchTimeScope.LastYear,
		text: localize("_PastYear")
	},
	{
		value: SharePointSearchTimeScope.OlderThenAYear,
		text: localize("_OlderThanAYear")
	}
];

type FilterContext = {
	timeScope: SharePointSearchTimeScope;
};

const useDataSource = createRemoteDataSource<
	ResultItem,
	FilterContext,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<ResultItem>> => {
				const {
					searchTerm,
					skip,
					amount,
					signal,
					filterState: {
						timeScope
					},
					sorting
				} = options;

				if (!searchTerm) {
					return {
						items: [],
						hasMore: false
					};
				}

				const page: LazyLoadPageResult<EgnyteGlobalSearchResultItem> = await searchEgnyte(
					{
						searchTerm,
						skip,
						take: amount,
						timeScope,
						order: convertSorting(sorting)
					},
					signal
				);

				return {
					items: page.items
						.map(setClientId),
					hasMore: page.hasMore
				};
			}
		}
	);

const propertyOrderMap: Partial<Record<keyof ResultItem, EgnyteSortProperty>> = {
	["name"]: EgnyteSortProperty.Name,
	["lastModifiedDate"]: EgnyteSortProperty.ModifiedDate
};

function convertSorting(
	sorting: DataSourceSortableState<ResultItem> | undefined
): EgnyteOrderModel | null {
	let order: EgnyteOrderModel | null = null;

	if (sorting && sorting.direction !== DataSourceSortDirection.None) {
		const property: EgnyteSortProperty | undefined = propertyOrderMap[sorting.propertyName];
		if (isNil(property)) {
			throw new Error(`Sorting on '${sorting.propertyName}' is not supported`);
		}

		order = {
			property,
			direction: convertSortDirectionToDto(sorting.direction)
		};
	}

	return order;
}

const egnyteSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Egnyte
};